import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../lab-dynamic-table";
import DateRangeSelector from "../DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";

interface DoctorShareData {
  _id: string;
  doctorName: string;
  totalAmount: number;
  totalDiscount: number;
  doctorShare: number;
  patientCount: number;
}

export const DoctorShareSummaryTab: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const [summaryData, setSummaryData] = useState<DoctorShareData[]>([]);
  const [loading, setLoading] = useState(false);

  // State for date filtering
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  
  const [doctors, setDoctors] = useState<any[]>([]);
  const [doctorId, setDoctorId] = useState<string>("");

  // State for pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [totalCount, setTotalCount] = useState<number>(0);

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    setPage(0);
  };

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getDrShareRevenue`,
        {
          params: {
            fromDate,
            toDate,
            doctorId,
            pageNo: page + 1,
            count: rowsPerPage,
          },
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setSummaryData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch doctor share summary");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching doctor share summary");
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummaryData();
  }, [fromDate, toDate, page, rowsPerPage, doctorId]);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getAllSharerDoctors`,
        {
          params: { pageNo: 1, count: 500 },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDoctors(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExcelExport = () => {
    if (!summaryData || summaryData?.length === 0) {
      toast.warn("No data to export.");
      return;
    }
    const worksheetData = summaryData?.map(row => ({
      "Doctor Name": row.doctorName,
      "Total Amount": row.totalAmount,
      "Doctor Share": row.doctorShare,
      "Total Discount": row.totalDiscount,
      "Patient Count": row.patientCount,
    }));

    const totalAmount = summaryData.reduce((sum, item) => sum + (Number(item?.totalAmount) || 0), 0);
    const totalDiscount = summaryData.reduce((sum, item) => sum + (Number(item?.totalDiscount) || 0), 0);
    const totalDoctorShare = summaryData.reduce((sum, item) => sum + (Number(item?.doctorShare) || 0), 0);
    const totalPatients = summaryData.reduce((sum, item) => sum + (Number(item?.patientCount) || 0), 0);

    worksheetData.push({
      "Doctor Name": "TOTAL",
      "Total Amount": totalAmount,
      "Doctor Share": totalDoctorShare,
      "Total Discount": totalDiscount,
      "Patient Count": totalPatients,
    });

    const worksheet = xlsx.utils.json_to_sheet(worksheetData);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "DoctorShareSummary");
    xlsx.writeFile(workbook, `Doctor_Share_Summary_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!summaryData || summaryData.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("DOCTOR SHARE SUMMARY REPORT", pageWidth / 2, yPosition, {
        align: "center",
      });
    
    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "DOCTOR NAME",
        "TOTAL AMOUNT",
        "DOCTOR SHARE",
        "TOTAL DISCOUNT",
        "PATIENT COUNT",
      ],
    ];

    const body = summaryData?.map(row => [
      row.doctorName,
      formatCurrency(row.totalAmount || 0),
      formatCurrency(row.doctorShare || 0),
      formatCurrency(row.totalDiscount || 0),
      row.patientCount,
    ]);

    const totalAmount = summaryData.reduce((sum, item) => sum + (Number(item?.totalAmount) || 0), 0);
    const totalDiscount = summaryData.reduce((sum, item) => sum + (Number(item?.totalDiscount) || 0), 0);
    const totalDoctorShare = summaryData.reduce((sum, item) => sum + (Number(item?.doctorShare) || 0), 0);
    const totalPatients = summaryData.reduce((sum, item) => sum + (Number(item?.patientCount) || 0), 0);

    body.push([
      "TOTAL",
      formatCurrency(totalAmount),
      formatCurrency(totalDoctorShare),
      formatCurrency(totalDiscount),
      totalPatients.toString(),
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: { 
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'center',
        valign: 'middle',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function(data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [25, 55, 85];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 5.8;
          data.cell.styles.lineColor = [200, 200, 200];
          data.cell.styles.lineWidth = 0.3;
          (data.cell.styles as any).cellPadding = { top: 2.5, right: 2, bottom: 2.5, left: 2 };
        }
      },
      didDrawPage: function(data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;
        
        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  const tableData = summaryData?.map(item => [
    item?.doctorName,
    formatCurrency(item?.totalAmount),
    formatCurrency(item?.doctorShare),
    formatCurrency(item?.totalDiscount),
    item?.patientCount,
  ]);

  const totalAmount = summaryData.reduce(
    (sum, item) => sum + item.totalAmount,
    0
  );
  const totalDiscount = summaryData.reduce(
    (sum, item) => sum + item.totalDiscount,
    0
  );
  const totalDoctorShare = summaryData.reduce(
    (sum, item) => sum + item.doctorShare,
    0
  );
  const totalPatients = summaryData.reduce(
    (sum, item) => sum + item.patientCount,
    0
  );

  const tableDataWithTotals = [
    ...tableData,
    [
      <strong>Totals</strong>,
      <strong>{formatCurrency(totalAmount)}</strong>,
      <strong>{formatCurrency(totalDoctorShare)}</strong>,
      <strong>{formatCurrency(totalDiscount)}</strong>,
      <strong>{totalPatients}</strong>,
    ],
  ];

  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FileDownload />}
            onClick={handleExcelExport}
            sx={gradientButtonStyle}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={gradientButtonStyle}
          >
            Print
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Select Doctor
            </Typography>
            <Autocomplete
              size="small"
              fullWidth
              options={doctors}
              getOptionLabel={option => option.fullName}
              value={doctors.find(doc => doc._id === doctorId) || null}
              onChange={(event, newValue) => {
                setDoctorId(newValue ? newValue._id : "");
              }}
              sx={{ minWidth: { xs: "100%", sm: "200px" } }}
              renderInput={params => (
                <TextField {...params} className="custom-input" />
              )}
            />
          </Box>
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          maxHeight: "60vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={[
            "Doctor Name",
            "Total Amount",
            "Doctor Share",
            "Total Discount",
            "Patient Count",
          ]}
          data={tableDataWithTotals}
          loading={loading}
          height={"60vh"}
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};
